import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

interface Props extends WidgetProps {}

export interface BrandDetailsLogoStyles {
    container?: InjectableCss;
    contentWrapper?: InjectableCss;
    linksWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    heading?: TypographyProps;
    link?: LinkProps;
    hiddenBelow?: InjectableCss;
    hiddenAbove?: InjectableCss;
}

export const logoStyles: BrandDetailsLogoStyles = {
    heading: {
        variant: "h1",
        css: css`
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    container: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    contentWrapper: {
        css: css`
            ${xScreen("lte", "sm")} {
                margin: 1rem 0;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 200px;
            height: 200px;
            margin-right: 40px;
        `,
    },
    image: {
        css: css`
            width: 100%;
            img {
                height: 100%;
            }
        `,
    },
    linksWrapper: {
        css: css`
            margin-top: auto;
            display: flex;
            justify-content: flex-end;
            gap: 22px;
            ${xScreen("lte", "sm")} {
                justify-content: flex-start;
                gap: 12px;
            }
            ${xScreen("lte", "xs")} {
                justify-content: flex-start;
                flex-direction: column;
                gap: 0;
            }
        `,
    },
    link: {
        typographyProps: {
            size: 17,
        },
    },
    hiddenBelow: {
        css: css`
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    hiddenAbove: {
        css: css`
            display: none;
            ${xScreen("lte", "sm")} {
                display: block;
            }
        `,
    },
};

const styles = logoStyles;

const BrandDetailsLogo: FC<Props> = () => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.hiddenBelow}>
                <BrandLogo brand={brand} />
            </StyledWrapper>
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-direction: column;
                `}
            >
                <Typography {...styles.heading}>{brand.name}</Typography>
                <StyledWrapper {...styles.hiddenAbove}>
                    <BrandLogo brand={brand} />
                </StyledWrapper>
                <BrandContent />
                <StyledWrapper {...styles.linksWrapper}>
                    <Link data-test-selector="brandToProductPageLink" href={brand.productListPagePath} {...styles.link}>
                        {translate("Shop All {0} Products").replace("{0}", brand.name)}
                    </Link>
                    <Link data-test-selector="brandExternalUrlLink" href={brand.externalUrl} {...styles.link}>
                        {translate("Visit {0} Website").replace("{0}", brand.name)}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const BrandLogo = ({ brand }: { brand: BrandModel }) => {
    if (!brand?.logoLargeImagePath) {
        return null;
    }

    return (
        <StyledWrapper {...styles.imageWrapper}>
            <LazyImage
                src={brand.logoLargeImagePath}
                altText={brand.logoAltText}
                {...styles.image}
                height="200"
                width="200"
            />
        </StyledWrapper>
    );
};

const BrandContent = () => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand?.htmlContent) {
        return null;
    }

    let updatedHtmlContent = brand.htmlContent;
    const bodyTag = brand.htmlContent.indexOf("<body");
    if (bodyTag > -1) {
        updatedHtmlContent = `<div${brand.htmlContent.substring(
            bodyTag + 5,
            brand.htmlContent.indexOf("</body>"),
        )}</div>`;
    }

    return <StyledWrapper {...styles.contentWrapper}>{parse(updatedHtmlContent, parserOptions)}</StyledWrapper>;
};

const widgetModule: WidgetModule = {
    component: BrandDetailsLogo,
    definition: {
        group: "Brand Details",
        icon: "diamond",
        displayName: "Brand Header",
        allowedContexts: ["BrandDetailsPage"],
    },
};

export default widgetModule;
